import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {ProTable} from "@ant-design/pro-components";
import {Avatar, Button, Modal, Popover, Row, Space, Spin, Tag, Tooltip, message} from "antd";
import {ProColumns} from "@ant-design/pro-table";
import DeleteCreator from "./DeleteCreator";
import {EditOutlined, EyeOutlined, HistoryOutlined, PlusOutlined, RobotOutlined, TagsOutlined, WarningOutlined} from "@ant-design/icons";
import FetchHistoryModal from "./FetchHistoryModal";
import {Link, Outlet, useLocation, useNavigate} from "react-router-dom";
// import NotifyCreatorManager from "./NotifyCreatorManager";
import ExportCreatorsExcel from "./ExportCreatorsExcel";
import ExportCreatorsGoogleSheet from "./ExportCreatorsGoogleSheet";
import './Creators.css';
import DeleteBulkCreators from "./DeleteBulkCreators";
import AddBulkCreatorTags from "./AddBulkCreatorTags";
import AddCreatorsToCampaign from "./AddCreatorsToCampaign";
import {creatorsAtom, filterSearchAtom, pageAtom, pageSizeAtom, sortFieldAtom, sortOrderAtom, identityFilterAtom, selectedCreatorIdsAtom, tagFiltersAtom, youtubeFollowersRangeAtom, instagramFollowersRangeAtom, tiktokFollowersRangeAtom, creatorTagsAtomById} from "./atoms";
import AISearchBar, { AISearchResult } from './AISearchBar';
import {useAtom, useAtomValue} from "jotai";
import {queryClientAtom} from "jotai-tanstack-query";
import {generate} from '@ant-design/colors';
import {TagFilter} from "./TagFilter";
import {TagFilterSidebar} from "./TagFilterSidebar";
import axiosApiInstance from "../../../api/axiosClient";
import {
    formatFollowers,
    formatLastFetchAt,
    formatSocialMediaAccountName,
    getPaletteColor,
    TitleIG,
    TitleTT,
    TitleYT,
    toEUR
} from "./utils";
import {useCan, useIsRole} from "../../../context/auth_atoms";
import CreatorDetailContent from './CreatorDetailContent';
// import { createFromIconfontCN } from '@ant-design/icons';
// const IconFont = createFromIconfontCN({
//     scriptUrl: [
//         '//at.alicdn.com/t/font_1788044_0dwu4guekcwr.js', // icon-javascript, icon-java, icon-shoppingcart (overridden)
//         '//at.alicdn.com/t/font_1788592_a5xf2bdic3u.js', // icon-shoppingcart, icon-python
//     ],
// });
type Props = {};


const Creators = (props: Props) => {
    //
    // State - Constants
    //
    // const actionRef = useRef<ActionType>();
    const [selectedRowKeys, setSelectedRowKeys] = useAtom(selectedCreatorIdsAtom);
    const [categoriesFilters, setCategoriesFilters] = useState(undefined);
    const [employeesFilters, setEmployeesFilters] = useState(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showingAll, setShowingAll] = useState<boolean>(false);
    const [visibleItems, setVisibleItems] = useState<number>(100);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate()
    const location = useLocation()
    const can = useCan();
    const is = useIsRole();

    const [{data: creators, isPending, isError}] = useAtom(creatorsAtom);
    const [page, setPage] = useAtom(pageAtom);
    const [pageSize, setPageSize] = useAtom(pageSizeAtom);
    const [sortField, setSortField] = useAtom(sortFieldAtom);
    const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);

    const queryClient = useAtomValue(queryClientAtom);
    const [filterSearch, setFilterSearch] = useAtom(filterSearchAtom)
    const [identityFilter, setIdentityFilter] = useAtom(identityFilterAtom);
    const [isSelectingAll, setIsSelectingAll] = useState<boolean>(false);
    
    const tagFilters = useAtomValue(tagFiltersAtom);
    const youtubeFollowersRange = useAtomValue(youtubeFollowersRangeAtom);
    const instagramFollowersRange = useAtomValue(instagramFollowersRangeAtom);
    const tiktokFollowersRange = useAtomValue(tiktokFollowersRangeAtom);

    // Fetch history modal state
    const [historyAccountId, setHistoryAccountId] = useState<number | null>(null);
    const [historyAccountName, setHistoryAccountName] = useState<string>('');

    // Creator detail modal state
    const [selectedCreatorId, setSelectedCreatorId] = useState<number | null>(null);

    // AI search state
    const [aiSearchResult, setAiSearchResult] = useState<AISearchResult | null>(null);
    const [{data: tagOptions}] = useAtom(creatorTagsAtomById(0));
    const isAiMode = aiSearchResult !== null;

    // Gender AI batch state
    const [genderBatchRunning, setGenderBatchRunning] = useState(false);

    const runGenderBatch = async () => {
        Modal.confirm({
            title: 'Auto-tag Creator Gender via AI',
            content: 'This will send all creators (in batches of 100) to GPT to infer gender and assign Female / Male / Unknown tags. This may take several minutes. Continue?',
            icon: <RobotOutlined />,
            okText: 'Run',
            cancelText: 'Cancel',
            visible: false,
            onOk: async () => {
                try {
                    setGenderBatchRunning(true);
                    await axiosApiInstance.post('/api/creators/gender-tags/run');
                    message.info('Gender tagging job started in the background.');
                    // Poll progress every 5 s
                    const poll = setInterval(async () => {
                        try {
                            const { data } = await axiosApiInstance.get('/api/creators/gender-tags/progress');
                            if (data.status === 'done') {
                                clearInterval(poll);
                                setGenderBatchRunning(false);
                                message.success(`Gender tagging complete: ${data.processed} processed, ${data.errors} errors.`);
                                queryClient.invalidateQueries({ queryKey: ['creators'] });
                            }
                        } catch { clearInterval(poll); setGenderBatchRunning(false); }
                    }, 5000);
                } catch (e: any) {
                    const msg = e?.response?.data?.error ?? e?.message ?? 'Failed to start batch';
                    message.error(msg);
                    setGenderBatchRunning(false);
                }
            },
        });
    };

    const handleSelectAllFiltered = async () => {
        setIsSelectingAll(true);
        try {
            // Fetch all creator IDs matching current filters
            const response = await axiosApiInstance.get('/api/creators/ids', {
                params: {
                    search: filterSearch,
                    tag_filters: tagFilters,
                    sort_field: sortField,
                    sort_order: sortOrder,
                    main_platform_id: identityFilter,
                    youtube_followers_min: youtubeFollowersRange[0],
                    youtube_followers_max: youtubeFollowersRange[1],
                    instagram_followers_min: instagramFollowersRange[0],
                    instagram_followers_max: instagramFollowersRange[1],
                    tiktok_followers_min: tiktokFollowersRange[0],
                    tiktok_followers_max: tiktokFollowersRange[1],
                }
            });
            
            // response.data should be an array of IDs
            if (Array.isArray(response.data)) {
                setSelectedRowKeys(response.data);
            } else {
                console.error('Unexpected response format:', response.data);
            }
        } catch (error: any) {
            console.error('Failed to select all creators:', error?.response?.data || error?.message || error);
        } finally {
            setIsSelectingAll(false);
        }
    };

    // Suppress ResizeObserver errors
    useEffect(() => {
        const resizeObserverErrHandler = (e: any) => {
            if (e?.message && e.message.includes('ResizeObserver loop')) {
                const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
                if (resizeObserverErr) {
                    resizeObserverErr.style.display = 'none';
                }
            }
        };
        window.addEventListener('error', resizeObserverErrHandler);
        
        const originalConsoleError = console.error;
        console.error = (...args) => {
            if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
                return;
            }
            originalConsoleError.apply(console, args);
        };
        
        return () => {
            window.removeEventListener('error', resizeObserverErrHandler);
            console.error = originalConsoleError;
        };
    }, []);

    // Handle scroll for incremental loading
    useEffect(() => {
        if (!showingAll) return;

        const handleScroll = () => {
            // Check if near bottom (within 500px)
            const scrollPosition = window.scrollY + window.innerHeight;
            const scrollHeight = document.documentElement.scrollHeight;
            
            if (scrollHeight - scrollPosition < 500 && visibleItems < (creators?.total || 0)) {
                const newVisibleItems = Math.min(visibleItems + 100, creators?.total || visibleItems);
                setVisibleItems(newVisibleItems);
                setPageSize(newVisibleItems);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [showingAll, visibleItems, creators?.total]);

    // const {
    //     employees,
    //     setEmployees,
    // } = useContext(GlobalStateContext);

    const TagsColumnFormat = ({creator}) => {
        const [visibleTags, setVisibleTags] = useState([]);
        const [hiddenTagsCount, setHiddenTagsCount] = useState(0);
        const [popoverWidth, setPopoverWidth] = useState(240);
        const tagContainerRef = useRef(null);

        useEffect(() => {
            const calculateVisibleTags = () => {
                if (!tagContainerRef.current) return;
                
                const tags = creator.tags || [];
                const primaryColors = tags.map(tag => tag.TagCategory.color);
                const palettes = primaryColors.map(color => generate(color));
                const appropriateColors = palettes.map((palette, index) => getPaletteColor(primaryColors[index], palette));

                const containerWidth = tagContainerRef.current.offsetWidth;
                const tagWidths = tags.map((tag, index) => {
                    const tagElement = document.createElement('div');
                    tagElement.style.display = 'inline-block';
                    tagElement.style.padding = '0 8px';
                    tagElement.style.borderRadius = '2px';
                    tagElement.style.backgroundColor = primaryColors[index];
                    tagElement.style.color = appropriateColors[index];
                    tagElement.textContent = tag.TagCategoryValue.value;
                    document.body.appendChild(tagElement);
                    const width = tagElement.offsetWidth + 8; // Include margin
                    document.body.removeChild(tagElement);
                    return width;
                });

                const grayTagWidth = 60; // Approximate width for the "+ <number> ..." tag
                let visibleTags = [];
                let totalWidth = 0;
                let lastVisibleIndex = -1;

                // Always include the first tag
                if (tags.length > 0) {
                    totalWidth += tagWidths[0];
                    visibleTags.push(
                        <Tooltip key={tags[0].id} title={tags[0].TagCategory.category_name}>
                            <Tag
                                color={primaryColors[0]}
                                style={{color: appropriateColors[0]}}
                            >
                                {tags[0].TagCategoryValue.value}
                            </Tag>
                        </Tooltip>
                    );
                    lastVisibleIndex = 0;
                }

                // Include as many tags as possible within the container width
                for (let i = 1; i < tags.length; i++) {
                    if (totalWidth + tagWidths[i] + grayTagWidth <= containerWidth) {
                        totalWidth += tagWidths[i];
                        visibleTags.push(
                            <Tooltip key={tags[i].id} title={tags[i].TagCategory.category_name}>
                                <Tag
                                    color={primaryColors[i]}
                                    style={{color: appropriateColors[i]}}
                                >
                                    {tags[i].TagCategoryValue.value}
                                </Tag>
                            </Tooltip>
                        );
                        lastVisibleIndex = i;
                    } else {
                        break;
                    }
                }

                // Adjust if the gray tag doesn't fit
                if (totalWidth + grayTagWidth > containerWidth) {
                    totalWidth -= tagWidths[lastVisibleIndex];
                    visibleTags.pop();
                    lastVisibleIndex--;
                }

                // If the first tag alone doesn't fit with the gray tag, move it to extra tags
                if (tags.length > 0 && tagWidths[0] + grayTagWidth > containerWidth) {
                    visibleTags = [];
                    lastVisibleIndex = -1;
                }

                setVisibleTags(visibleTags);
                setHiddenTagsCount(tags.length - (lastVisibleIndex + 1));

                // Calculate popover width based on the maximum tag line length
                let currentLineWidth = 0;
                let maxLineWidth = 0;

                const hiddenTags = tags.slice(lastVisibleIndex + 1);
                hiddenTags.forEach((tag, index) => {
                    currentLineWidth += tagWidths[lastVisibleIndex + 1 + index];
                    if (currentLineWidth > 240) { // Assume the default width
                        maxLineWidth = Math.max(maxLineWidth, currentLineWidth - tagWidths[lastVisibleIndex + 1 + index]);
                        currentLineWidth = tagWidths[lastVisibleIndex + 1 + index];
                    }
                });

                maxLineWidth = Math.max(maxLineWidth, currentLineWidth);

                setPopoverWidth(Math.min(maxLineWidth + 10, 240)); // Add 10 pixels for padding
            };

            calculateVisibleTags();
            const resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    calculateVisibleTags();
                });
            });
            if (tagContainerRef.current) {
                resizeObserver.observe(tagContainerRef.current);
            }
            return () => {
                resizeObserver.disconnect();
            };
        }, [creator.tags]);

        return (
            <div ref={tagContainerRef}
                 style={{display: 'flex', alignItems: 'center', width: "inherit", marginRight: "-60px"}}>
                <Space direction="horizontal">
                    <Row justify={"space-evenly"}>
                        {visibleTags}
                        {hiddenTagsCount > 0 && (
                            <Popover
                                content={
                                    <Space
                                        direction="horizontal"
                                        wrap
                                        style={{
                                            width: `${popoverWidth}px`,
                                            marginRight: "-18px"
                                        }}
                                    >
                                        <Row
                                            justify={"space-evenly"}
                                            // justify={"center"}
                                            style={{marginBottom: "-9px"}}
                                        >
                                            {creator.tags.slice(visibleTags.length).map(tag => (
                                                <Tooltip key={tag.id} title={tag.TagCategory.category_name}>
                                                    <Tag
                                                        color={tag.TagCategory.color}
                                                        style={{
                                                            color: getPaletteColor(tag.TagCategory.color, generate(tag.TagCategory.color)),
                                                            marginBottom: "9px"
                                                        }}
                                                    >
                                                        {tag.TagCategoryValue.value}
                                                    </Tag>
                                                </Tooltip>
                                            ))}
                                        </Row>
                                    </Space>
                                }
                                title="More Tags"
                            >
                                <Tag color="#bfbfbf">+{hiddenTagsCount} ...</Tag>
                            </Popover>
                        )}
                    </Row>
                </Space>
            </div>
        );
    };

    const AI_FEATURES_ENABLED = true; // set to true to re-enable AI search + gender tagging
    const isLidl = is('Lidl User') || window.location.origin?.includes('lidl');
    const columns: ProColumns<API.Creators.Creator>[] = [
        {
            align: 'center',
            render: (_, record) => {
                // first account's image
                if (!record.accounts || record.accounts.length === 0) {
                    return null
                }
                const img = record.accounts[0].image;

                return <Avatar
                    src={`data:image/jpeg;base64,${img}`}
                    style={{width: 34, height: 34, cursor: 'pointer'}}
                    shape={"circle"}
                    onClick={() => setSelectedCreatorId(record.id)}
                />
            },
            // width: 70,
            // width: '2%',
        },
        {
            title: 'Creator',
            dataIndex: 'creator',
            align: 'center',
            width: '8%',
            sorter: true,
            render: (_, record) => (
                <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCreatorId(record.id)}
                >
                    {record.creator}
                </span>
            ),
        },
        {
            title: 'Connections',
            dataIndex: 'contacts',
            key: 'contacts',
            align: 'center',
            width: '10%',
            hideInSearch: true,
            render: (_, record) => {
                const list = record.contacts ?? [];
                if (list.length === 0) return <span style={{color: '#bbb'}}>—</span>;
                return (
                    <Space size={[4, 4]} wrap>
                        {list.map((c) => (
                            <Tag
                                key={c.id}
                                color="blue"
                                style={{cursor: 'pointer', margin: 0}}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/social/connections/view/${c.id}`);
                                }}
                            >
                                {c.full_name?.trim() || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || '(unnamed)'}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Identity',
            dataIndex: 'main_platform_id',
            valueType: 'select',
            align: 'center',
            width: '8%',
            filters: true,
            onFilter: true,
            valueEnum: {
                1: {text: 'YouTube'},
                2: {text: 'Instagram'},
                3: {text: 'TikTok'},
            },
            // filters: true,
            render: (_, record) => record.main_platform.characterization
        },
        {
            title: 'Followers',
            dataIndex: 'followers',
            // width: "50%",
            // sorter: true,
            hideInSearch: true,
            align: 'center',
            width: '16%',
            children: [
                {
                    title: TitleYT(''),
                    dataIndex: 'youtube_pricing',
                    align: 'center',

                    key: 'youtube_followers',
                    render: (dom, record) => record.accounts.filter(account => account.platform.name === "YouTube").map((account) => (
                        <Space direction="vertical" style={{display: 'flex', alignItems: 'center'}}>
                            <Tooltip title={<>{formatSocialMediaAccountName(account.key)}<br/>{formatLastFetchAt(account.last_fetch_at)}{account.has_error && <><br/><span style={{color: '#faad14'}}>⚠ Last fetch failed</span></>}</>}>
                                <a href={account.key} target="_blank" rel="noreferrer"
                                   style={{color: account.has_error ? "#faad14" : "darkslategrey"}}>{account.has_error && <WarningOutlined style={{marginRight: 4}}/>}{formatFollowers(account.following)}</a>
                            </Tooltip>
                            <HistoryOutlined style={{fontSize: 11, color: '#999', cursor: 'pointer'}} onClick={() => { setHistoryAccountId(account.id); setHistoryAccountName(formatSocialMediaAccountName(account.key)); }} />
                        </Space>
                    )),
                    sorter: true

                        //
                        // Compare by the total number of followers of each creator
                        //
                        // // Calculate the total number of followers of each creator
                        // let sum_a = 0
                        // for (let i = followers_a.length - 1; i >= 0; i--) {
                        //     sum_a = sum_a + followers_a[i]
                        // }
                        //
                        // let sum_b = 0
                        // for (let i = followers_b.length - 1; i >= 0; i--) {
                        //     sum_b = sum_b + followers_b[i]
                        // }
                        //
                        // // Compare the two sums to sort the table, based on each creator's total followers
                        // if (sum_a > sum_b) {
                        //     return 1;
                        // } else if (sum_a < sum_b) {
                },
                {
                    title: TitleIG(''),
                    dataIndex: 'instagram_pricing',
                    align: 'center',

                    key: 'instagram_followers',
                    render: (dom, record) => record.accounts.filter(account => account.platform.name === "Instagram").map((account) => (
                        <Space direction="vertical" style={{display: 'flex', alignItems: 'center'}}>
                            <Tooltip title={<>{formatSocialMediaAccountName(account.key)}<br/>{formatLastFetchAt(account.last_fetch_at)}{account.has_error && <><br/><span style={{color: '#faad14'}}>⚠ Last fetch failed</span></>}</>}>
                                <a href={account.key} target="_blank" rel="noreferrer"
                                   style={{color: account.has_error ? "#faad14" : "darkslategrey"}}>{account.has_error && <WarningOutlined style={{marginRight: 4}}/>}{formatFollowers(account.following)}</a>
                            </Tooltip>
                            <HistoryOutlined style={{fontSize: 11, color: '#999', cursor: 'pointer'}} onClick={() => { setHistoryAccountId(account.id); setHistoryAccountName(formatSocialMediaAccountName(account.key)); }} />
                        </Space>
                    )),
                    sorter: true
                },
                {
                    title: TitleTT(''),
                    dataIndex: 'tiktok_pricing',
                    align: 'center',

                    key: 'tiktok_followers',
                    render: (dom, record) => record.accounts.filter(account => account.platform.name === "TikTok").map((account) => (
                        <Space direction="vertical" style={{display: 'flex', alignItems: 'center'}}>
                            <Tooltip title={<>{formatSocialMediaAccountName(account.key)}<br/>{formatLastFetchAt(account.last_fetch_at)}{account.has_error && <><br/><span style={{color: '#faad14'}}>⚠ Last fetch failed</span></>}</>}>
                                <a href={account.key} target="_blank" rel="noreferrer"
                                   style={{color: account.has_error ? "#faad14" : "darkslategrey"}}>{account.has_error && <WarningOutlined style={{marginRight: 4}}/>}{formatFollowers(account.following)}</a>
                            </Tooltip>
                            <HistoryOutlined style={{fontSize: 11, color: '#999', cursor: 'pointer'}} onClick={() => { setHistoryAccountId(account.id); setHistoryAccountName(formatSocialMediaAccountName(account.key)); }} />
                        </Space>
                    )),
                    sorter: true
                },
            ],
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            align: 'center',
            width: '16%',
            search: false,
            render: (_, creator) => <TagsColumnFormat creator={creator}/>,
            hideInTable: isLidl,
        },
        {
            title: 'Pricings',
            dataIndex: 'pricings',
            align: 'center',
            width: '40%',
            children: [
                {
                    title: TitleYT(''),
                    dataIndex: 'youtube_pricing',
                    align: 'center',
                    children: [
                        {
                            title: 'Video',
                            dataIndex: 'youtube_video_pricing',
                            key: 'youtube_video',
                            align: 'center',
                            renderText: (_text, record) => toEUR(record.pricing.youtube_video),
                            sorter: true
                        }
                    ]
                },
                {
                    title: ''
                    // render: () => <Divider type='vertical' />
                },
                {
                    title: TitleIG(''),
                    dataIndex: 'instagram_pricing',
                    align: 'center',
                    children: [
                        {
                            title: 'Reel',
                            dataIndex: 'instagram_reel_pricing',
                            key: 'instagram_reel',
                            align: 'center',
                            renderText: (_text, record) => toEUR(record.pricing.instagram_reel),
                            sorter: true
                        },
                        {
                            title: 'Post',
                            dataIndex: 'instagram_post_pricing',
                            key: 'instagram_post',
                            align: 'center',
                            renderText: (_text, record) => toEUR(record.pricing.instagram_post),
                            sorter: true
                        },
                        {
                            title: 'Story',
                            dataIndex: 'instagram_story_pricing',
                            key: 'instagram_story',
                            align: 'center',
                            renderText: (_text, record) => toEUR(record.pricing.instagram_story),
                            sorter: true
                        }
                    ]
                },
                {
                    title: ''
                    // render: () => <Divider type='vertical' />
                },
                {
                    title: TitleTT(''),
                    dataIndex: 'tiktok_pricing',
                    align: 'center',
                    children: [
                        {
                            title: 'Video',
                            dataIndex: 'tiktok_video_pricing',
                            key: 'tiktok_video',
                            align: 'center',
                            renderText: (_text, record) => toEUR(record.pricing.tiktok_video),
                            sorter: true
                        }
                    ]
                },
                {
                    title: ''
                },
                
            ]
        },
        {
            title: 'Deal Types',
            dataIndex: 'deal_types_pricing',
            align: 'center',
            children: [
                {
                    title: <Tooltip title="Exclusivity"><span>EX</span></Tooltip>,
                    dataIndex: 'exclusivity_pricing',
                    key: 'exclusivity',
                    align: 'center',
                    renderText: (_text, record) => toEUR(record.pricing.exclusivity)
                },
                {
                    title: <Tooltip title="Paid Ads/Sponsorship"><span>PA</span></Tooltip>,
                    dataIndex: 'paid_ads_sponsorship_pricing',
                    key: 'paid_ads_sponsorship',
                    align: 'center',
                    renderText: (_text, record) => toEUR(record.pricing.paid_ads_sponsorship)
                },
                {
                    title: <Tooltip title="Collaboration"><span>CO</span></Tooltip>,
                    dataIndex: 'collaboration_pricing',
                    key: 'collaboration',
                    align: 'center',
                    renderText: (_text, record) => toEUR(record.pricing.collaboration)
                }
            ]
        },
        {
            title: '',
            width: '8%',
            render: (_, record) => <Space>
                <Link to={`${record.id}`}>
                    <Tooltip title={'View'}><Button icon={<EyeOutlined/>} type={"text"}/></Tooltip>
                </Link>
                <Link to={`${record.id}/edit`}>
                    <Tooltip title={'Edit'}><Button icon={<EditOutlined/>} type={"text"}/></Tooltip>
                </Link>
                {/*<CreatorViewEdit editable={false} creator={record} key={record.id + '_view'}/>*/}
                {/*<CreatorViewEdit editable={true} creator={record} key={record.id + '_edit'}/>*/}
                {/*<NotifyCreatorManager id={record.id} key={record.id}/>*/}
                {can('delete', 'creators::data') && <DeleteCreator id={record.id} key={record.id}/>}
            </Space>,
            hideInTable: isLidl,
        }
    ];

    return (
        <>
            <div style={{ display: 'flex', height: '100%' }}>
                <TagFilterSidebar />
                <div style={{ flex: 1, marginLeft: '30px' }}>
                    {AI_FEATURES_ENABLED && !isLidl && (
                        <AISearchBar
                            onResults={setAiSearchResult}
                            tagOptions={tagOptions}
                        />
                    )}
                    <ProTable<API.Creators.Creator>
                columns={columns as any}
                // actionRef={creatorsTableRef}
                // dataSource={creators}
                loading={isAiMode ? false : isPending}
                dataSource={isAiMode ? aiSearchResult!.data : creators?.data}
                ghost
                rowKey={record => record.id}
                // search={{
                //     labelWidth: 'auto',
                // }}
                search={false}
                sticky={{
                    offsetHeader: 55,
                }}
                onChange={(pagination, filters, sorter: any, extra) => {
                    if (filters.main_platform_id){
                        setIdentityFilter(filters.main_platform_id as string[]);
                    } else {
                        setIdentityFilter(undefined);
                    }
                    // Only handle sorting changes, not pagination
                    if (extra.action === 'sort') {
                        if (sorter && sorter.order) {
                            // Get the key from the column
                            const sortKey = sorter.column?.key || sorter.field || sorter.columnKey;
                            console.log('Sorting by:', sortKey, 'Order:', sorter.order, 'Full sorter:', sorter);
                            setSortField(sortKey);
                            setSortOrder(sorter.order);
                            setPage(1); // Reset to first page when sorting changes
                        } else {
                            // Clear sorting
                            setSortField(undefined);
                            setSortOrder(undefined);
                        }
                    }
                }}
                options={{
                    // setting: {
                    //     listsHeight: 400,
                    // },
                    reload: () => {
                        // queryClient.invalidateQueries({queryKey: ['creators', 'items']})
                        return queryClient.invalidateQueries({queryKey: ['creators']})
                    },
                    search: {
                        placeholder: 'Please enter keyword', allowClear: true,
                        onChange: (e) => {
                            // console.log('search', e)
                            setFilterSearch(e.target.value)
                            setPage(1);
                        },
                        value: filterSearch,
                    },
                }}
                pagination={{
                    pageSize: isAiMode ? (aiSearchResult?.total ?? 200) : pageSize,
                    current: isAiMode ? 1 : page,
                    total: isAiMode ? aiSearchResult?.total : creators?.total,
                    onChange: (page, pageSize) => {
                        setPage(page)
                        setPageSize(pageSize)
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: true,
                }}
                // request={async (params = {}, sort, filter) =>
                //     // axiosApiInstance.get<CreatorRow[]>('/api/creators/', {params})
                // {
                //     // console.log('on request')
                //     // console.log(params)
                //     console.log(filter)
                //     // console.log(sort)
                //     const res = await axiosApiInstance.get<API.Creators.Creator[]>('/api/creators/items', {
                //         params:
                //             {
                //                 ...filter, ...params,
                //                 sort_communication_date: sort.date_of_communication,
                //                 sort_creation_date: sort.created_at,
                //                 sort_accounts: sort.accounts_stringified
                //             }
                //     })
                //     // console.log(res.data)
                //     // await fetchCategoriesData()
                //     // await fetchEmployeesData()
                //     return Promise.resolve({data: res.data, success: true, total: res.data?.length})
                // }
                // }
                //TODO
                // onDataSourceChange={dataSource => {
                //     // console.log('on ds change', dataSource)
                //     setCreators(dataSource)
                //     // setFilters()
                //     setCategoriesFilters(creatorsCategories?.map(value => ({
                //         text: value.name,
                //         value: value.id
                //     })))
                //     setEmployeesFilters(employees?.map(value => ({
                //         text: value.full_name,
                //         value: value.id
                //     })))
                // }}
                // onLoad={dataSource => {
                //     // console.log('on load')
                //     // setCreatorsItems(dataSource)
                // }}
                // pagination={{
                //     pageSize: 7,
                //     // pageSize: 150,
                //     hideOnSinglePage: true,
                //     showSizeChanger: false,
                //     // onChange: (page) => console.log(page),
                // }}
                dateFormatter="string"
                toolBarRender={() => [
                    !isLidl && <TagFilter key="tag-filter"/>,
                    !isLidl && <Button key="button" icon={<PlusOutlined/>} type="primary"
                                       onClick={() => navigate('/creators/list/new', {state: {background: location}})}
                    >
                        New
                    </Button>,
                    can('export', 'leads::data') && <ExportCreatorsExcel/>, // correct permission, do not change
                    can('export', 'leads::data') && (
                        <ExportCreatorsGoogleSheet
                            key="export-google-sheet"
                            selectedIds={selectedRowKeys}
                        />
                    ),
                    // AI_FEATURES_ENABLED && can('write', 'creators::data') && !isLidl && (
                    //     <Tooltip key="gender-tags" title="Auto-detect & tag creator gender using AI">
                    //         <Button
                    //             icon={<TagsOutlined />}
                    //             loading={genderBatchRunning}
                    //             onClick={runGenderBatch}
                    //         >
                    //             {genderBatchRunning ? 'Tagging…' : 'Tag Gender (AI)'}
                    //         </Button>
                    //     </Tooltip>
                    // ),
                    // <CreatorCreate/>
                ]}
                tableAlertRender={() => {
                    const allSelected = creators?.total && selectedRowKeys.length === creators.total;
                    return (
                        <Space size={24}>
                          <span>
                                Selected {selectedRowKeys.length} {selectedRowKeys.length > 1 ? 'creators' : 'creator'}
                                {creators?.data && selectedRowKeys.some(id => !creators.data.map(c => c.id).includes(id)) && 
                                    <span style={{color: '#888'}}> (some not visible in current filter)</span>
                                }
                                {!allSelected && creators?.total && selectedRowKeys.length > 0 && (
                                    <a 
                                        style={{
                                            marginLeft: 8,
                                            cursor: isSelectingAll ? 'not-allowed' : 'pointer',
                                            opacity: isSelectingAll ? 0.5 : 1
                                        }}
                                        onClick={isSelectingAll ? undefined : handleSelectAllFiltered}
                                    >
                                        {isSelectingAll ? 'Loading...' : `Select all ${creators.total} creators`}
                                    </a>
                                )}
                          </span>
                        </Space>
                    )
                }}
                tableAlertOptionRender={() => {
                    return (
                        <Space size={16}>
                            {selectedRowKeys.length > 0 && (
                                <AddCreatorsToCampaign
                                    creatorIds={selectedRowKeys}
                                    onClearSelection={() => setSelectedRowKeys([])}
                                />
                            )}
                            {selectedRowKeys.length && (
                                <AddBulkCreatorTags 
                                    creatorsIds={selectedRowKeys}
                                    onClearSelection={() => {
                                        setSelectedRowKeys([]);
                                    }}
                                />
                            )}
                            {/* <DeleteBulkCreators creatorsIds={selectedRowKeys}
                                                onClearSelection={() => {
                                                    setSelectedRowKeys([]);
                                                }}/> */}
                            <a style={{marginInlineStart: 8}} onClick={() => {
                                setSelectedRowKeys([]);
                            }}>
                                Clear selection
                            </a>
                            {/*<a>Delete batch</a>*/}
                        </Space>
                    );
                }}
                rowSelection={!isLidl && {
                    selectedRowKeys,
                    onChange: (keys: React.Key[], selectedRows: API.Creators.Creator[]) => {
                        // Get currently visible creator IDs
                        const visibleIds = creators?.data?.map(c => c.id) || [];
                        
                        // Remove previously selected IDs that are now visible but not in keys
                        const notSelectedAnymore = selectedRowKeys.filter(id => 
                            visibleIds.includes(id) && !keys.includes(id)
                        );
                        
                        // Add newly selected keys
                        const newlySelected = (keys as number[]).filter(id => !selectedRowKeys.includes(id));
                        
                        // Update selection: remove deselected + add newly selected
                        const updatedSelection = [
                            ...selectedRowKeys.filter(id => !notSelectedAnymore.includes(id)),
                            ...newlySelected
                        ];
                        
                        setSelectedRowKeys(updatedSelection);
                    },
                    // renderCell: (value, record, index, originNode) => {
                    //     return <Row justify={"center"} align="middle" style={{height: 46}}>
                    //         <Col>
                    //             {originNode}
                    //         </Col>
                    //     </Row>
                    // },
                }}
                columnsState={{
                    persistenceKey: 'creator-creators-list',
                    persistenceType: 'localStorage',
                    // "Contacts" column is hidden by default — users can
                    // reveal it via the table's column-settings toggle menu.
                    defaultValue: {
                        contacts: {show: false},
                    },
                }}
            />
                </div>
            </div>

            <Outlet/>

            <FetchHistoryModal
                accountId={historyAccountId}
                accountName={historyAccountName}
                open={historyAccountId !== null}
                onClose={() => setHistoryAccountId(null)}
            />

            <Modal
                open={selectedCreatorId !== null}
                onCancel={() => setSelectedCreatorId(null)}
                width={1000}
                title="Creator Details"
                footer={null}
                destroyOnClose
            >
                {selectedCreatorId !== null && (
                    <React.Suspense fallback={<div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>}>
                        <CreatorDetailContent id={String(selectedCreatorId)} />
                    </React.Suspense>
                )}
            </Modal>
        </>
    );
};

export default Creators;